import { addDays, taskState, type Checkin, type LocalDate, type Occurrence } from '@planer/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ClipboardList, Keyboard, Plus, Settings, Sun } from 'lucide-react';
import { MotionConfig } from 'motion/react';
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ItemFormResult } from './components/ItemForm';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Sheet } from './components/Sheet';
import { Toaster } from './components/Toaster';
import { useScheduleActions } from './lib/actions';
import { api, onUnauthorized } from './lib/api';
import { mergeCheckins } from './lib/checkinState';
import { queryKeys, usePlan, useSchedule, useToday, useWeek } from './lib/hooks';
import { startOutboxSync } from './lib/outbox';
import { syncPushSubscription } from './lib/push';
import { clearLocalData } from './lib/queryClient';
import { closeSheet, isCompact, openSheet, useSheet, type SheetState } from './lib/sheets';
import { useShortcuts } from './lib/shortcuts';
import { useApplyTheme } from './lib/theme';
import { showToast } from './lib/toast';
import { TodayScreen } from './screens/TodayScreen';

// Loaded on demand, so the first screen needs less JavaScript (all chunks are precached for offline use).
const LoginScreen = lazy(() =>
  import('./screens/LoginScreen').then((m) => ({ default: m.LoginScreen })),
);
const WeekScreen = lazy(() =>
  import('./screens/WeekScreen').then((m) => ({ default: m.WeekScreen })),
);
const PlanScreen = lazy(() =>
  import('./screens/PlanScreen').then((m) => ({ default: m.PlanScreen })),
);
const SettingsScreen = lazy(() =>
  import('./screens/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
);
const ItemForm = lazy(() => import('./components/ItemForm').then((m) => ({ default: m.ItemForm })));
const DetailSheet = lazy(() =>
  import('./components/DetailSheet').then((m) => ({ default: m.DetailSheet })),
);
const PlanSheet = lazy(() =>
  import('./components/plan/PlanSheet').then((m) => ({ default: m.PlanSheet })),
);

export type Tab = 'today' | 'week' | 'plan' | 'settings';

/**
 * Messages for the error codes of a calendar login redirect. Only fixed codes travel in the URL,
 * so a crafted link cannot put its own text on this site.
 */
const OAUTH_ERRORS: Record<string, string> = {
  potekla: 'Prijava je potekla. Poskusi znova.',
  zavrnjeno: 'Dostop ni bil odobren.',
  ponudnik: 'Ponudnik je zavrnil prijavo (morda zahteva odobritev skrbnika).',
  nastavitve: 'Ponudnik ne sprejme prijave: preveri Client ID, Client secret in Redirect URI.',
  povezava: 'Povezava koledarja ni uspela.',
};

/** Result of a calendar login redirect (`/?koledarji=povezano|napaka&razlog=…`), read once. */
const oauthReturn = (() => {
  const params = new URLSearchParams(window.location.search);
  const result = params.get('koledarji');
  if (!result) return null;
  const code = params.get('razlog') ?? '';
  return {
    ok: result === 'povezano',
    reason: Object.hasOwn(OAUTH_ERRORS, code) ? OAUTH_ERRORS[code] : null,
  };
})();

export function App() {
  useApplyTheme();
  const qc = useQueryClient();
  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: api.session,
    retry: false,
    // Always re-check on start (the cached value only bridges offline starts).
    staleTime: 0,
  });
  const [loggedOut, setLoggedOut] = useState(false);

  useEffect(
    () =>
      onUnauthorized(() => {
        setLoggedOut(true);
        void clearLocalData();
      }),
    [],
  );

  const needsLogin = loggedOut || session.data?.authenticated === false;

  let content: ReactNode;
  if (needsLogin) {
    content = (
      <LoginScreen
        onLoggedIn={() => {
          setLoggedOut(false);
          void qc.invalidateQueries();
        }}
      />
    );
  } else if (!session.data) {
    content = (
      <main className="grid h-full place-items-center px-6 text-center text-muted">
        {session.isError ? 'Strežnik ni dosegljiv. Poskusi znova kasneje.' : 'Nalagam …'}
      </main>
    );
  } else {
    content = (
      // Last line of defence: a crash outside a single screen still shows a way out.
      <ErrorBoundary>
        <Planner
          onLoggedOut={() => {
            setLoggedOut(true);
          }}
        />
      </ErrorBoundary>
    );
  }
  return (
    <MotionConfig reducedMotion="user">
      <Suspense fallback={null}>{content}</Suspense>
    </MotionConfig>
  );
}

const TABS: { tab: Tab; label: string; icon: (size: number) => ReactNode; key: string }[] = [
  { tab: 'today', label: 'Danes', icon: (s) => <Sun size={s} />, key: '1' },
  { tab: 'week', label: 'Teden', icon: (s) => <CalendarDays size={s} />, key: '2' },
  { tab: 'plan', label: 'Načrt', icon: (s) => <ClipboardList size={s} />, key: '3' },
  { tab: 'settings', label: 'Nastavitve', icon: (s) => <Settings size={s} />, key: '4' },
];

function NavButton({
  active,
  label,
  icon,
  badge,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: ReactNode;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={`tap relative flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-2xl text-[11px] font-semibold transition-colors ${
        active ? 'text-accent' : 'text-muted'
      }`}
    >
      {icon}
      {label}
      {badge ? (
        <span className="absolute top-1.5 left-1/2 ml-2 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-bold text-on-accent">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function SideNav({ tab, inbox, onTab }: { tab: Tab; inbox: number; onTab: (t: Tab) => void }) {
  return (
    <aside
      aria-label="Stranska vrstica"
      className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface px-3 py-5 lg:flex"
    >
      <p className="flex items-center gap-2 px-3 pb-5 text-[19px] font-bold tracking-tight">
        <span className="grid h-8 w-8 place-items-center rounded-xl bg-accent text-on-accent">
          <Sun size={18} />
        </span>
        Planer
      </p>
      <button
        type="button"
        onClick={() => {
          openSheet({ type: 'add', quick: '' });
        }}
        className="tap mb-4 flex min-h-11 items-center gap-2 rounded-xl bg-accent px-3.5 text-[15px] font-semibold text-on-accent shadow-[var(--shadow-card)] hover:brightness-105"
      >
        <Plus size={18} strokeWidth={2.5} /> Nova aktivnost
        <kbd className="ml-auto border-white/30 bg-white/15 text-on-accent">N</kbd>
      </button>
      <nav aria-label="Glavna navigacija" className="flex flex-col gap-0.5">
        {TABS.map((t) => (
          <button
            key={t.tab}
            type="button"
            aria-current={tab === t.tab ? 'page' : undefined}
            onClick={() => {
              onTab(t.tab);
            }}
            className={`tap group flex min-h-11 items-center gap-3 rounded-xl px-3 text-[15px] font-medium transition-colors ${
              tab === t.tab ? 'bg-accent-soft text-accent-ink' : 'text-text-2 hover:bg-surface-2'
            }`}
          >
            {t.icon(19)}
            {t.label}
            {t.tab === 'plan' && inbox > 0 && (
              <span className="ml-auto rounded-full bg-accent px-1.5 text-[11px] font-bold text-on-accent">
                {inbox}
              </span>
            )}
            {!(t.tab === 'plan' && inbox > 0) && (
              <kbd className="ml-auto opacity-0 transition-opacity group-hover:opacity-100">
                {t.key}
              </kbd>
            )}
          </button>
        ))}
      </nav>
      <button
        type="button"
        onClick={() => {
          openSheet({ type: 'shortcuts' });
        }}
        className="tap mt-auto flex min-h-10 items-center gap-2 rounded-xl px-3 text-[13px] text-muted hover:bg-surface-2"
      >
        <Keyboard size={16} /> Bližnjice <kbd className="ml-auto">?</kbd>
      </button>
    </aside>
  );
}

function Planner({ onLoggedOut }: { onLoggedOut: () => void }) {
  const qc = useQueryClient();
  const today = useToday();
  const [tab, setTabState] = useState<Tab>(oauthReturn ? 'settings' : 'today');
  const [selected, setSelected] = useState<LocalDate | null>(null);
  const sheet = useSheet();
  const schedule = useSchedule();
  const plan = usePlan();
  const actions = useScheduleActions();
  const day = selected ?? today;
  const week = useWeek(day, today);
  const scroller = useRef<HTMLDivElement>(null);
  const inbox = (plan.data?.tasks ?? []).filter((t) => {
    const s = taskState(t, today);
    return s === 'inbox' || s === 'overdue';
  }).length;

  const setTab = (t: Tab) => {
    setTabState(t);
    if (t === 'today') setSelected(null);
  };

  // Back from a Google/Microsoft login: show the result in Settings (tab is preset below).
  useEffect(() => {
    if (!oauthReturn) return;
    window.history.replaceState(null, '', '/');
    void qc.invalidateQueries({ queryKey: queryKeys.calendars });
    void qc.invalidateQueries({ queryKey: ['events'] });
    showToast(
      oauthReturn.ok
        ? { message: 'Koledar je povezan.', tone: 'done' }
        : { message: oauthReturn.reason ?? 'Povezava koledarja ni uspela.', tone: 'missed' },
      6000,
    );
  }, [qc]);

  // Push: keep this device registered; refresh after "Opravljeno" in a notification.
  useEffect(() => {
    void syncPushSubscription().catch(() => undefined);
    const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
    const onMessage = (e: MessageEvent<{ type?: string }>) => {
      if (e.data.type === 'planer:refresh') {
        void qc.invalidateQueries({ queryKey: ['checkins'] });
        void qc.invalidateQueries({ queryKey: queryKeys.plan });
      }
    };
    sw?.addEventListener('message', onMessage);
    return () => {
      sw?.removeEventListener('message', onMessage);
    };
  }, [qc]);

  // Every tab starts at the top (they share one scroll container).
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [tab]);

  useEffect(
    () =>
      startOutboxSync((applied: Checkin[]) => {
        // Put delivered check-ins into every cached week first, then confirm with the server.
        for (const query of qc.getQueryCache().findAll({ queryKey: ['checkins'] })) {
          const [, from, to] = query.queryKey as [string, string, string];
          qc.setQueryData<{ checkins: Checkin[] }>(query.queryKey, (old) =>
            mergeCheckins(old, { from, to }, applied),
          );
        }
        void qc.invalidateQueries({ queryKey: ['checkins'] });
        void qc.invalidateQueries({ queryKey: queryKeys.streaks });
        // A checked-off time box completes its inbox task.
        void qc.invalidateQueries({ queryKey: queryKeys.plan });
      }),
    [qc],
  );

  const select = (d: LocalDate) => {
    setSelected(d === today ? null : d);
  };
  const openDay = (d: LocalDate) => {
    closeSheet();
    select(d);
    setTabState('today');
  };

  useShortcuts({
    enabled: sheet === null,
    onNew: () => {
      openSheet({ type: 'add', quick: '', date: tab === 'today' ? day : undefined });
    },
    onNewTask: () => {
      openSheet({ type: 'task', task: null });
    },
    onTab: setTab,
    onToday: () => {
      setSelected(null);
    },
    onStep: (dir) => {
      if (tab === 'today') select(addDays(day, dir));
      else if (tab === 'week') select(addDays(day, dir * 7));
    },
    onHelp: () => {
      openSheet({ type: 'shortcuts' });
    },
    onWizard: () => {
      openSheet({ type: 'wizard' });
    },
  });

  return (
    <div className="flex h-full">
      <SideNav tab={tab} inbox={inbox} onTab={setTab} />
      <div ref={scroller} className="h-full min-w-0 flex-1 overflow-y-auto">
        {/* A new key per tab: a crashed screen gets a fresh start when you switch. */}
        <ErrorBoundary key={tab}>
          {tab === 'today' && (
            <TodayScreen
              today={today}
              selected={day}
              onSelect={select}
              onOpen={(occ) => {
                openSheet({ type: 'detail', occ });
              }}
              onQuickAdd={(quick) => {
                openSheet({ type: 'add', quick });
              }}
              onAddAt={(date, time) => {
                openSheet({ type: 'add', quick: '', date, time });
              }}
            />
          )}
          <Suspense fallback={null}>
            {tab === 'week' && (
              <WeekScreen
                today={today}
                selected={day}
                onSelectWeek={select}
                onOpenDay={openDay}
                onOpen={(occ) => {
                  openSheet({ type: 'detail', occ });
                }}
                onAddAt={(date, time) => {
                  openSheet({ type: 'add', quick: '', date, time });
                }}
              />
            )}
            {tab === 'plan' && <PlanScreen today={today} />}
            {tab === 'settings' && <SettingsScreen onLoggedOut={onLoggedOut} />}
          </Suspense>
        </ErrorBoundary>
      </div>

      <nav
        aria-label="Glavna navigacija"
        className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/90 backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto grid max-w-xl grid-cols-5 items-center px-2 pt-1">
          {TABS.slice(0, 2).map((t) => (
            <NavButton
              key={t.tab}
              active={tab === t.tab}
              label={t.label}
              icon={t.icon(22)}
              onClick={() => {
                setTab(t.tab);
              }}
            />
          ))}
          <div className="flex justify-center">
            <button
              type="button"
              aria-label="Dodaj"
              onClick={() => {
                openSheet({ type: 'add', quick: '' });
              }}
              className="tap -mt-7 grid h-[3.75rem] w-[3.75rem] place-items-center rounded-full bg-accent text-on-accent shadow-[var(--shadow-float)]"
            >
              <Plus size={30} strokeWidth={2.5} />
            </button>
          </div>
          {TABS.slice(2).map((t) => (
            <NavButton
              key={t.tab}
              active={tab === t.tab}
              label={t.label}
              icon={t.icon(22)}
              badge={t.tab === 'plan' ? inbox : undefined}
              onClick={() => {
                setTab(t.tab);
              }}
            />
          ))}
        </div>
      </nav>

      <Sheet
        open={sheet !== null}
        onClose={closeSheet}
        compact={isCompact(sheet)}
        label={sheetLabel(sheet)}
      >
        <Suspense fallback={<div className="h-72" aria-hidden />}>
          <SheetContent
            sheet={sheet}
            today={today}
            day={day}
            week={week}
            schedule={schedule.data}
            goals={plan.data?.goals ?? []}
            actions={actions}
            onOpenDay={openDay}
            onShowWeek={(weekStart) => {
              closeSheet();
              select(weekStart);
              setTabState('week');
            }}
          />
        </Suspense>
      </Sheet>
      <Toaster />
    </div>
  );
}

function sheetLabel(sheet: SheetState | null): string {
  switch (sheet?.type) {
    case 'detail':
      return 'Podrobnosti';
    case 'wizard':
      return 'Načrtuj teden';
    case 'move':
      return 'Premik';
    case 'shortcuts':
      return 'Bližnjice';
    default:
      return 'Urejanje';
  }
}

interface SheetContentProps {
  sheet: SheetState | null;
  today: LocalDate;
  day: LocalDate;
  week: ReturnType<typeof useWeek>;
  schedule: ReturnType<typeof useSchedule>['data'];
  goals: NonNullable<ReturnType<typeof usePlan>['data']>['goals'];
  actions: ReturnType<typeof useScheduleActions>;
  onOpenDay: (date: LocalDate) => void;
  onShowWeek: (weekStart: LocalDate) => void;
}

function SheetContent({
  sheet,
  today,
  day,
  week,
  schedule,
  goals,
  actions,
  onOpenDay,
  onShowWeek,
}: SheetContentProps) {
  const itemOf = (occ: Occurrence) => schedule?.items.find((i) => i.id === occ.itemId);
  const busy = actions.create.isPending || actions.update.isPending || actions.exception.isPending;

  function submitAdd({ input }: ItemFormResult) {
    actions.create.mutate(input, {
      onSuccess: (item) => {
        closeSheet();
        showToast({ message: `Dodano: ${item.title}`, tone: 'done' });
      },
    });
  }

  function submitEdit(occ: Occurrence, { scope, input }: ItemFormResult) {
    const done = { onSuccess: closeSheet };
    // Template/task links are not edited from here.
    const { templateId: _t, taskId: _k, ...changes } = input;
    if (scope === 'this') {
      actions.exception.mutate(
        {
          id: occ.itemId,
          date: occ.originDate,
          input: {
            action: 'move',
            newTime: input.startTime,
            newDurationMin: input.durationMin ?? null,
            newDate: input.startDate === occ.originDate ? null : input.startDate,
          },
        },
        done,
      );
    } else if (scope === 'following') {
      actions.update.mutate(
        { id: occ.itemId, patch: { scope: 'following', fromDate: occ.originDate, changes } },
        done,
      );
    } else {
      actions.update.mutate({ id: occ.itemId, patch: { scope: 'all', changes } }, done);
    }
  }

  if (!sheet) return null;
  switch (sheet.type) {
    case 'add':
      return (
        <ItemForm
          key={`add-${sheet.quick}-${sheet.date ?? ''}-${sheet.time ?? ''}-${sheet.goalId ?? ''}`}
          today={day}
          initialQuickText={sheet.quick}
          initialTime={sheet.time}
          initialDate={sheet.date}
          initialGoalId={sheet.goalId}
          goals={goals}
          busy={busy}
          onSubmit={submitAdd}
          onCancel={closeSheet}
        />
      );
    case 'detail':
      return (
        <DetailSheet
          occ={sheet.occ}
          item={itemOf(sheet.occ)}
          status={week.status(sheet.occ.key)}
          today={today}
          onClose={closeSheet}
          onEdit={(scope) => {
            openSheet({ type: 'edit', occ: sheet.occ, scope });
          }}
          onSkip={() => {
            actions.skipOne(sheet.occ);
            closeSheet();
          }}
          onDelete={(scope) => {
            void actions.remove(sheet.occ, scope);
            closeSheet();
          }}
        />
      );
    case 'edit':
      return (
        <ItemForm
          key={`edit-${sheet.occ.key}`}
          today={today}
          item={itemOf(sheet.occ) ?? null}
          occurrence={sheet.occ}
          initialScope={sheet.scope}
          goals={goals}
          busy={busy}
          onSubmit={(result) => {
            submitEdit(sheet.occ, result);
          }}
          onCancel={closeSheet}
        />
      );
    default:
      return (
        <PlanSheet
          sheet={sheet}
          onClose={closeSheet}
          onOpenDay={onOpenDay}
          onShowWeek={onShowWeek}
        />
      );
  }
}
